import { createBreakpoints } from '@chakra-ui/theme-tools'
import { extendTheme } from '@chakra-ui/react'
import { brandColors, formColors } from 'shared/basicPalette'
import { getRgbFromHex } from 'shared/colorUtils'
import { colorsExtensionType } from '../public-api/hooks/useLooopTheme'

const breakpointsBase = createBreakpoints({
  sm: '540px',
  md: '1024px',
  lg: '1248px',
  xl: '1500px'
})

// Solving custom breakpoints issue (https://github.com/chakra-ui/chakra-ui/issues/374)
// now it finally behaves as described with definition of array or object [100,200] or {sm:100, md:200}
export let breakpoints = {
  0: breakpointsBase.base,
  1: breakpointsBase.sm,
  2: breakpointsBase.md,
  3: breakpointsBase.lg,
  4: breakpointsBase.xl,
  ...breakpointsBase
}

export const fonts = {
  heading: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  body: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  mono: 'Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace'
}

export const sizes = {
  container: {
    sm: breakpointsBase.sm,
    md: breakpointsBase.md,
    lg: breakpointsBase.lg,
    xl: breakpointsBase.xl
  }
}

export const fontWeights = {
  hairline: 100,
  thin: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900
}

export const fontSizes = {
  xs: '11px',
  ssm: '12px',
  sm: '13px',
  md: '15px',
  lg: '16px',
  xl: '18px',
  '2xl': '24px',
  '3xl': '28px',
  '4xl': '36px',
  '5xl': '48px',
  '6xl': '64px'
}

export const radii = {
  none: '0',
  sm: '2px',
  base: '4px',
  md: '4px',
  lg: '8px',
  xl: '16px',
  full: '9999px'
}

export const defaultColors = {
  backgroundColor: 'gray',
  ...brandColors,
  formItems: formColors
}

const zIndexes = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 11,
  sticky: 12,
  banner: 13,
  overlay: 14,
  modal: 15,
  popover: 16,
  skipLink: 17,
  toast: 18,
  tooltip: 19
}

export const getFocusStyle = (color: string, inset: boolean = false) => {
  const primaryMidRgb = getRgbFromHex(color)

  return {
    outline: 'none', // remove default style
    boxShadow: `0 0 0 4px ${inset ? 'inset' : ''} rgba(${primaryMidRgb.r}, ${
      primaryMidRgb.g
    }, ${primaryMidRgb.b}, .3)`
  }
}

export const getCustomTheme = (extendBrandColors?: colorsExtensionType) => {
  const colors = { ...defaultColors, ...extendBrandColors }

  return extendTheme({
    // if you change this, also change BREAK_MOBILE const in react components
    breakpoints,
    fonts,
    fontWeights,
    zIndices: zIndexes,
    fontSizes,
    sizes,
    radii,
    colors,
    shadows: {
      focus: getFocusStyle(colors.buttonPrimary.mid).boxShadow,
      outline: `0 0 0 1px white, 0 0 0 2px ${brandColors.grey[400]}`
    },
    components: {
      Alert: {
        baseStyle: {
          container: {
            borderRadius: radii.lg
          }
        }
      },
      Accordion: {
        variants: {
          clean: {
            button: {
              _hover: {
                bg: 'transparent'
              }
            }
          }
        }
      },
      Button: {
        baseStyle: {
          fontSize: fontSizes.md,
          fontWeight: fontWeights.normal,
          color: colors.grey[500],
          background: colors.grey[75],
          lineHeight: '20px',
          borderRadius: radii.base,
          borderWidth: 0,
          borderStyle: 'solid',
          appearance: undefined,
          userSelect: undefined,
          transition: 'all 0.2s',
          display: 'inline-flex',
          alignItems: 'center',
          textAlign: 'center',
          cursor: 'pointer',
          _focusVisible: getFocusStyle(colors.buttonPrimary.mid)
        },
        sizes: {
          xs: { fontSize: fontSizes.xs, px: '6px', py: '2px' },
          sm: { fontSize: fontSizes.sm, px: '10px', py: '6px' },
          md: { fontSize: fontSizes.md, px: '14px', py: '10px' },
          lg: { fontSize: fontSizes.lg, px: '16px', py: '12px' }
        }
      },

      CloseButton: {
        baseStyle: {
          _focusVisible: {
            boxShadow: 'none'
          }
        }
      },

      Drawer: {
        baseStyle: {
          zIndex: 99,
          header: {
            fontWeight: 400,
            fontSize: '20px',
            borderBottom: `1px solid ${defaultColors.grey['200']}`
          },
          overlay: {
            bgColor: 'rgba(200, 210, 210, 0.5)'
          }
        }
      },

      Table: {
        sizes: {
          md: {
            th: {
              p: '16px 8px',
              fontSize: 'ssm'
            },
            td: {
              p: '16px 8px',
              fontSize: 'md'
            },
            tr: {
              _focusVisible: getFocusStyle(colors.buttonPrimary.mid)
            },
            caption: {
              p: '16px',
              fontSize: 'md',
              fontWeight: 'normal'
            }
          }
        },
        variants: {
          simple: {
            table: {
              bg: colors.white,
              borderRadius: '4px'
            },
            tr: {
              p: '0 8px'
            },
            th: {
              bg: colors.white,
              fontWeight: 400,
              textTransform: 'none',
              letterSpacing: 'normal',
              color: colors.grey[500],
              _hover: {
                color: colors.grey[900]
              },
              _focusVisible: getFocusStyle(colors.buttonPrimary.mid, true)
            }
          },
          unstyled: {
            tr: {
              border: 'none',
              _first: {
                borderTop: 'none'
              }
            },
            th: {
              fontSize: 'inherit',
              fontWeight: 'inherit',
              textTransform: 'inherit',
              letterSpacing: 'inherit',
              p: '8px'
            },
            td: {
              fontSize: 'inherit',
              p: '8px'
            }
          }
        }
      },

      Editable: {
        baseStyle: {
          preview: {
            py: 0,
            _hover: {
              bgColor: defaultColors.formItems.hoverBgColor
            }
          },
          input: {
            border: '1px solid',
            boxShadow: 'none',
            py: 0,
            _focusVisible: {
              boxShadow: 'none',
              borderColor: colors.buttonPrimary[400],
              bg: defaultColors.formItems.bgColor
            }
          }
        }
      },

      Link: {
        baseStyle: {
          _focusVisible: getFocusStyle(colors.buttonPrimary.mid)
        }
      },

      Form: {
        baseStyle: {
          helperText: {
            mt: '4px',
            fontSize: '12px',
            lineHeight: '20px'
          }
        }
      },

      FormLabel: {
        baseStyle: {
          fontSize: '15px',
          fontWeight: fontWeights.normal,
          lineHeight: '16px',
          color: colors.grey[500],
          marginTop: '12px',
          marginBottom: '4px'
        }
      },

      FormError: {
        baseStyle: {
          text: {
            marginTop: '4px',
            fontSize: '12px',
            lineHeight: '20px'
          }
        }
      },

      Heading: {
        sizes: {
          md: {
            fontWeight: fontWeights.medium
          },
          lg: {
            fontWeight: fontWeights.medium
          },
          xl: {
            fontWeight: fontWeights.medium
          }
        }
      },

      Input: {
        defaultProps: {
          variant: 'filled'
        },
        sizes: {
          md: {
            field: {
              fontSize: '14px',
              h: '40px',
              px: '8px',
              py: 0,
              _invalid: {
                borderWidth: '1px'
              }
            }
          },
          lg: {
            field: {
              fontSize: '24px',
              h: '46px',
              px: '8px',
              py: 0,
              _invalid: {
                borderWidth: '1px'
              }
            }
          }
        },
        variants: {
          outline: {
            field: {
              borderWidth: '1px',
              _focusVisible: {
                borderWidth: '1px',
                borderColor: colors.buttonPrimary.mid,
                boxShadow: 'none'
              }
            }
          },
          filled: {
            field: {
              borderWidth: '1px',
              borderRadius: radii.base,
              bgColor: defaultColors.formItems.bgColor,
              color: brandColors.grey.dark,
              _hover: { bgColor: defaultColors.formItems.hoverBgColor },
              _focusVisible: {
                borderWidth: '1px',
                borderColor: colors.buttonPrimary.mid,
                bgColor: defaultColors.formItems.bgColor,
                boxShadow: 'none'
              }
            }
          },
          unstyled: {
            field: {
              borderRadius: 'none',
              border: 'none',
              outline: 'none',
              bgColor: 'transparent'
            }
          }
        }
      },

      Textarea: {
        variants: {
          filled: {
            outline: 'none',
            outlineOffset: '0',
            borderWidth: '1px',
            borderRadius: radii.base,
            bg: defaultColors.formItems.bgColor,
            color: brandColors.grey.dark,
            _hover: { bg: defaultColors.formItems.hoverBgColor },
            _focusVisible: {
              borderWidth: '1px',
              borderColor: colors.buttonPrimary.mid,
              bg: defaultColors.formItems.focusBgColor,
              boxShadow: 'none'
            }
          }
        }
      },

      Menu: {
        baseStyle: {
          button: {
            px: '10px',
            py: '4px',
            bgColor: defaultColors.formItems.bgColor,
            border: '1px',
            borderColor: 'transparent',
            borderRadius: radii.md,
            color: defaultColors.grey[900],
            _focusVisible: getFocusStyle(colors.buttonPrimary.mid)
          },
          list: {
            py: '4px',
            bgColor: defaultColors.grey[50],
            borderRadius: radii.md,
            border: undefined,
            position: 'relative',
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.15)'
          },
          item: {
            px: '14px',
            py: '10px',
            border: 0,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            bgColor: defaultColors.grey[50],
            _hover: {
              bgColor: defaultColors.grey[100]
            },
            _focusVisible: {
              bgColor: defaultColors.grey[100]
            },
            _active: {
              bgColor: defaultColors.grey[100]
            }
          }
        }
      },

      Switch: {
        baseStyle: {
          track: {
            bgColor: colors.grey[400],
            _active: { bgColor: colors.grey[500] },
            _focusVisible: getFocusStyle(colors.buttonPrimary.mid)
          }
        },
        defaultProps: {
          size: 'lg'
        }
      },

      Checkbox: {
        baseStyle: {
          control: {
            border: '1px',
            borderColor: defaultColors.grey[400],
            _checked: {
              position: 'relative',
              _before: {
                content: '""',
                position: 'absolute',
                borderRadius: '0',
                width: '60%',
                height: '30%',
                background: 'transparent',
                top: '50%',
                left: '50%',
                border: '1px solid white',
                borderTop: 'none',
                borderRight: 'none',
                transform: 'translate(-50%, -70%) rotate(-50deg)'
              },
              _disabled: {
                opacity: 1
              }
            },
            _disabled: {
              borderColor: defaultColors.grey.light,
              backgroundColor: 'transparent',
              opacity: 0.35
            },
            _focus: getFocusStyle(colors.buttonPrimary.mid)
          }
        },
        sizes: {
          sm: {
            control: { width: '16px', height: '16px' },
            label: { fontSize: 'md' }
          },
          md: {
            control: { width: '24px', height: '24px' },
            label: { fontSize: 'md' }
          },
          lg: {
            control: { w: '42px', h: '42px' },
            label: { fontSize: 'lg' }
          }
        },
        variants: {
          rounded: {
            control: {
              borderRadius: '50%',
              _checked: {
                boxShadow: 'inset 0 0 0 2px white',
                _before: {
                  content: 'none'
                }
              }
            }
          }
        },
        defaultProps: {
          size: 'sm',
          colorScheme: 'buttonPrimary'
        }
      },

      Radio: {
        baseStyle: {
          label: {
            paddingLeft: '4px'
          },
          control: {
            cursor: 'pointer',
            border: '1px',
            mt: '3px', // Be in the middle of the line but still on top
            alignSelf: 'start',
            bg: defaultColors.grey[50],
            borderColor: defaultColors.grey[500],
            _checked: {
              position: 'relative',
              border: '0',
              boxShadow: `inset 0 0 0 1px ${colors.buttonPrimary[500]}, inset 0 0 0 3px ${colors.white}`,
              _before: {
                content: 'none'
              }
            },
            _focusVisible: {
              boxShadow: `${
                getFocusStyle(colors.buttonPrimary.mid).boxShadow
              }, inset 0 0 0 1px ${
                colors.buttonPrimary[500]
              }, inset 0 0 0 3px ${colors.white}`
            },
            _disabled: {
              borderColor: defaultColors.grey.light,
              backgroundColor: 'transparent',
              cursor: 'not-allowed',
              opacity: 0.35,
              _checked: {
                backgroundColor: colors.buttonPrimary[500]
              }
            }
          }
        },
        variants: {
          withTickbox: {
            control: {
              cursor: 'pointer',
              border: '1px',
              borderColor: defaultColors.grey[400],
              _checked: {
                position: 'relative',
                boxShadow: 'none',
                _before: {
                  content: '""',
                  position: 'absolute',
                  borderRadius: '0',
                  width: '60%',
                  height: '30%',
                  background: 'transparent',
                  top: '50%',
                  left: '50%',
                  border: '1px solid white',
                  borderTop: 'none',
                  borderRight: 'none',
                  transform: 'translate(-50%, -70%) rotate(-50deg)'
                },
                _disabled: {
                  opacity: 1
                }
              },
              _focusVisible: {
                boxShadow: 'none'
              },
              _disabled: {
                borderColor: defaultColors.grey.light,
                backgroundColor: 'transparent',
                cursor: 'not-allowed',
                opacity: 0.35
              }
            }
          }
        },
        sizes: {
          sm: {
            control: { width: '16px', height: '16px' },
            label: { fontSize: 'md' }
          },
          md: {
            control: { width: '24px', height: '24px' },
            label: { fontSize: 'md' },
            icon: { fontSize: '1rem' }
          },
          lg: {
            control: { width: '36px', height: '36px' },
            label: { fontSize: 'md' }
          }
        },
        defaultProps: {
          size: 'sm',
          colorScheme: 'buttonPrimary'
        }
      },

      Popover: {
        baseStyle: {
          popper: {
            zIndex: 1001
          }
        },
        parts: ['content', 'arrow'],
        variants: {
          tooltip: {
            content: {
              borderRadius: '4px',
              width: 'auto',
              bg: defaultColors.grey.dark,
              color: defaultColors.white,
              maxWidth: '260px',
              fontSize: '12px',
              lineHeight: '16px',
              '--popper-arrow-bg': defaultColors.grey.dark
            },
            body: {
              padding: '8px'
            }
          }
        }
      },

      Tabs: {
        baseStyle: {
          tab: {
            background: 'transparent',
            borderTop: 0,
            borderLeft: 0,
            borderRight: 0,
            borderTopRadius: '8px',
            color: defaultColors.grey['600'],
            _focus: {
              boxShadow: 'none'
            }
          }
        },
        sizes: {
          md: {
            tab: {
              paddingLeft: ['4px', '16px'],
              paddingRight: ['4px', '16px'],
              paddingTop: '14px',
              paddingBottom: '12px',
              fontSize: ['sm', 'md']
            }
          },
          lg: {
            tab: {
              h: '56px',
              paddingLeft: ['4px', '16px'],
              paddingRight: ['4px', '16px'],
              paddingTop: '19px',
              paddingBottom: '17px',
              fontSize: ['sm', 'md']
            }
          }
        },
        variants: {
          line: {
            tab: {
              mb: '0',
              _selected: {
                bgColor: defaultColors.white,
                color: colors.buttonPrimary[500],
                borderColor: colors.buttonPrimary[500]
              },
              _focusVisible: getFocusStyle(colors.buttonPrimary.mid, true)
            },
            tablist: {
              borderBottom: `1px solid ${defaultColors.grey['200']}`
            }
          }
        }
      },

      Spinner: {
        baseStyle: {
          borderWidth: '1px',
          color: defaultColors.grey[500]
        },
        sizes: {
          xs: {
            p: '10px'
          },
          sm: {
            p: '12px'
          },
          md: {
            p: '16px'
          },
          lg: {
            p: '24px'
          },
          xl: {
            p: '32px'
          }
        }
      }
    }
  })
}
